import { useEffect, useMemo, useState } from "react";
import { getDeviceHistory, getDeviceMonitorLatest } from "../../components/device/deviceApi";
import { createAnalyticsDateChunks } from "../../utils/analyticsDateRange";
import {
  applyMonitorReadings,
  historyRowsToSeriesPoints,
  SENSOR_UNITS,
} from "./vehicleTrack/liveTelemetry";

const ALLOWED_SENSOR_CHARTS = {
  temperature: {
    label: "Temperature",
    color: "#F18F01",
    unit: SENSOR_UNITS.temperature,
    yLabel: "Temperature (°C)",
  },
  humidity: {
    label: "Humidity",
    color: "#6B7AE5",
    unit: SENSOR_UNITS.humidity,
    yLabel: "Humidity (%)",
    domain: [0, 100],
  },
};

const formatAvg = (points, unit) => {
  if (!points.length) return "";
  const avg = points.reduce((sum, point) => sum + Number(point.value || 0), 0) / points.length;
  const digits = Number.isInteger(avg) ? 0 : 1;
  return `${avg.toFixed(digits)}${unit || ""}`;
};

const formatPointTime = (timestamp, includeDate) => {
  const date = new Date(timestamp);
  if (!Number.isFinite(date.getTime())) return "";
  if (includeDate) {
    return date.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getHistoryRange = (dateOption, customDateRange) => {
  const chunks = createAnalyticsDateChunks(dateOption, customDateRange);
  if (!chunks.length) return null;

  const start = new Date(`${chunks[0].customStartDate}T00:00:00`);
  const endChunk = chunks[chunks.length - 1];
  const end = new Date(`${endChunk.customEndDate}T23:59:59`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return { start, end };
};

const readingMatchesStop = (reading, stopIds = []) => {
  const ids = stopIds.filter(Boolean).map(String);
  if (!ids.length) return false;
  return [reading?.stopId, reading?.stop_id, reading?.stop?.id, reading?.stop?.stopId]
    .filter(Boolean)
    .some((id) => ids.includes(String(id)));
};

export default function useAnalyticsSensorCharts({
  vehicleId,
  stopId,
  stopCode,
  organizationId,
  dateOption,
  customDateRange,
}) {
  const [charts, setCharts] = useState([]);
  const includeDate = dateOption === "last 7 days" || dateOption === "custom";

  useEffect(() => {
    if (!vehicleId && !stopId) {
      setCharts([]);
      return undefined;
    }

    let isCancelled = false;
    const range = getHistoryRange(dateOption, customDateRange);
    if (!range) {
      setCharts([]);
      return undefined;
    }

    const loadSensorCharts = async () => {
      try {
        const monitor = await getDeviceMonitorLatest({
          ...(vehicleId ? { vehicleId } : {}),
          ...(stopId ? { stopId } : {}),
          organizationId,
        });
        const allReadings = Array.isArray(monitor?.data) ? monitor.data : [];
        const matchedStopReadings = stopId
          ? allReadings.filter((reading) =>
              readingMatchesStop(reading, [stopId, stopCode]),
            )
          : [];
        const fromSensors = applyMonitorReadings(
          stopId ? matchedStopReadings : allReadings,
        );
        const { sensors = [], ranges = {}, ...values } = fromSensors;
        const uniqueSensors = [];
        const seen = new Set();

        sensors.forEach((sensor) => {
          const type = sensor?.type;
          if (!ALLOWED_SENSOR_CHARTS[type] || seen.has(type)) return;
          seen.add(type);
          uniqueSensors.push(sensor);
        });

        Object.keys(ALLOWED_SENSOR_CHARTS).forEach((type) => {
          if (seen.has(type) || values[type] == null) return;
          const source = sensors.find((sensor) => sensor?.trackingDeviceId) || {};
          seen.add(type);
          uniqueSensors.push({
            type,
            trackingDeviceId: source.trackingDeviceId,
            deviceType: source.deviceType,
            sensorType: type,
            unit: ranges[type]?.unit || ALLOWED_SENSOR_CHARTS[type].unit,
            value: values[type],
          });
        });

        const nextCharts = (
          await Promise.all(
            uniqueSensors.map(async (sensor) => {
              if (!sensor.trackingDeviceId) return null;

              try {
                const response = await getDeviceHistory(sensor.trackingDeviceId, {
                  deviceType: sensor.deviceType,
                  sensorType: sensor.sensorType || sensor.type,
                  from: range.start.toISOString(),
                  to: range.end.toISOString(),
                  limit: 200,
                });
                const rows = Array.isArray(response?.data)
                  ? response.data
                  : Array.isArray(response)
                    ? response
                    : [];
                const points = historyRowsToSeriesPoints(rows, sensor.type).map((point) => ({
                  ...point,
                  time: formatPointTime(point.timestamp, includeDate),
                }));

                if (!points.length) return null;

                const config = ALLOWED_SENSOR_CHARTS[sensor.type];
                const unit = sensor.unit || config.unit || "";
                return {
                  key: sensor.type,
                  label: config.label,
                  unit,
                  color: config.color,
                  yLabel: config.yLabel,
                  domain: config.domain,
                  points,
                  avg: formatAvg(points, unit),
                };
              } catch (historyError) {
                console.error("Error fetching sensor analytics history:", historyError);
                return null;
              }
            }),
          )
        ).filter(Boolean);

        if (!isCancelled) setCharts(nextCharts);
      } catch (error) {
        console.error("Error fetching sensor analytics:", error);
        if (!isCancelled) setCharts([]);
      }
    };

    loadSensorCharts();
    return () => {
      isCancelled = true;
    };
  }, [
    vehicleId,
    stopId,
    stopCode,
    organizationId,
    dateOption,
    customDateRange?.startDate,
    customDateRange?.endDate,
    includeDate,
  ]);

  return useMemo(() => charts, [charts]);
}
